import { Alert } from "@core/components/ui/alert";
import Spinner from "@core/components/ui/Spinner";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faArrowRight, faBoxesStacked } from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Link } from "@remix-run/react";
import { useState } from "react";
import { useCategoryTree } from "@ecom/hooks/useCategoryTree";
import { H4 } from "@core/components/ui/typography";
import { ImageWithFallback } from "@core/ImageWithFallback";
import { cn } from "@core/lib/utils";
import { useTranslation } from "react-i18next";

interface ICategoriesMenuProps {
  extraOnClick?: () => void;
}
export const CategoriesMenu = (categoriesMenuProps: ICategoriesMenuProps) => {
  const [categoryId, setCategoryId] = useState<number | null>(null);
  const { t } = useTranslation();
  const { categories, loading } = useCategoryTree();
  if (loading) {
    return <div className="w-100 text-center p-6">
      <Spinner />
    </div>
  } else if (!categories) {
    return <div>
      <Alert color="error">Error loading categories</Alert>
    </div>
  }

  const showSubcategory = (id: number) => {
    setCategoryId(id);
  };

  const selectedCategory = categories.find(x => x.Id === categoryId);
  return <div className="flex">
    <div className="flex flex-col border-e p-2 pe-0 text-nowrap">
      <Link onClick={() => categoriesMenuProps?.extraOnClick?.()} to="/catalog" className="text-surface-800 tracking-wide text-xs p-1 me-2 hover:text-primary-600">{t("ui.BrowseCatalog", "Browse Catalog")}</Link>
      {categories.map((x) => (
        <Link onClick={() => categoriesMenuProps?.extraOnClick?.()}
          key={x.Id}
          to={`/catalog${x.SEOURL ?? "#"}`}
          className={cn(selectedCategory?.Id === x.Id ? "bg-surface-200" : "", "p-2 text-black text-base text-nowrap")}
          onMouseEnter={() => showSubcategory(x.Id)}
        >
          {x.Name}
        </Link>
      ))}
    </div>
    <div className="grow">
      {!selectedCategory && <div
        className="p-2 h-full w-full flex flex-col justify-center items-center">
        <FontAwesomeIcon
          icon={faBoxesStacked as IconProp}
          className="text-slate-300 h-10 w-10"
        />
        <h2 className="text-slate-300 font-bold">Choose a Category</h2>
      </div>}
      {selectedCategory && (
        <div
          className="h-full py-2 px-4">
          {selectedCategory.Children?.length ? (
            <>
              <div className="flex justify-between items-center text-black">
                <div className="text-xl font-bold">
                  {selectedCategory.Name}
                </div>
                <Link className="text-base font-bold text-primary hover:text-primary-600" to={`/catalog${selectedCategory.SEOURL}`} onClick={() => categoriesMenuProps?.extraOnClick?.()}>
                  View all{` ${selectedCategory.Name} `}
                  <FontAwesomeIcon icon={faArrowRight as IconProp} />
                </Link>
              </div>
              <ul className="grid grid-cols-4">
                {selectedCategory.Children?.map((y) => (
                  <div className="col-span-1" key={y.Id}>
                    <Link onClick={() => categoriesMenuProps?.extraOnClick?.()}
                      key={y.Id}
                      to={`/catalog${y.SEOURL ?? "#"}`}
                      title={y.Name}
                      className="font-bold text-nowrap text-black border-b block hover:text-primary-600"
                    >
                      <ImageWithFallback src={`/api/file/${y.PrimaryImageId}`} />
                      {y.Name}
                    </Link>
                    {y.Children !== undefined && y.Children?.length > 0
                      ? (
                        y.Children?.map((z) => (
                          <div className="col-span-1" key={z.Id}>
                            <Link onClick={() => categoriesMenuProps?.extraOnClick?.()}
                              key={z.Id}
                              to={`/catalog${z.SEOURL ?? "#"}`}
                              title={z.Name}
                              className="text-nowrap text-sm text-black hover:text-primary-600"
                            >
                              {z.Name}
                            </Link>
                          </div>
                        )))
                      : <span className="py-1 text-nowrap text-sm">
                        {t("ui.NoAdditionalCategories", "No Additional Categories")}
                      </span>
                    }
                  </div>
                ))}
              </ul>
            </>
          ) : (
            <div className="h-full">
              <div className="flex justify-between text-black items-center">
                <H4>{selectedCategory.Name}</H4>
                <Link onClick={() => categoriesMenuProps?.extraOnClick?.()}
                  className="text-2x font-bold text-link ps-1 hover:text-primary-600"
                  to={`/catalog${selectedCategory.SEOURL}`}
                >
                  View all&nbsp;
                  <FontAwesomeIcon icon={faArrowRight as IconProp} />
                </Link>
              </div>
              <p>{t("ui.NoAdditionalCategories", "No Additional Categories")}</p>
            </div>
          )}
        </div>
      )}
    </div>
  </div>
};
